import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import { SubmitFeedbackDocument } from "#app/lib/data";
import { APP_VERSION } from "#app/lib/app-version";
import {
  useFeatureFlags,
  FEATURE_FLAG_DEFINITION_LIST,
} from "#app/hooks/use-feature-flags";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * Submits in-app feedback. Stamps the current client environment (platform,
 * user agent, viewport, build version, and the workspace's resolved feature
 * flags) onto the submission and toasts the outcome. Server-side, core adds
 * live per-service versions before the Asana ticket is created. Returns
 * `true` when the feedback was accepted so the caller can close its dialog.
 */
export function useSubmitFeedback() {
  const [runSubmitFeedback, { loading }] = useMutation(SubmitFeedbackDocument);
  const { ready, hasFlag } = useFeatureFlags();

  const submitFeedback = useCallback(
    async (message: string): Promise<boolean> => {
      // The catalog is the known flag set; resolve each against this
      // workspace. Omitted (null) until flags have loaded — never report
      // all-off when the set simply hasn't resolved yet.
      const featureFlags = ready
        ? FEATURE_FLAG_DEFINITION_LIST.map(
            (def) => `${def.key}=${hasFlag(def.key) ? "on" : "off"}`,
          ).join(", ")
        : null;
      try {
        const { data } = await runSubmitFeedback({
          variables: {
            message,
            context: {
              // Web app is always "web"; native shells report ios/android.
              platform: "web",
              userAgent: navigator.userAgent,
              viewport: `${window.innerWidth}x${window.innerHeight}`,
              appVersion: APP_VERSION,
              featureFlags,
            },
          },
        });
        const result = data?.submitFeedback;
        if (result?.__typename === "FeedbackReceipt") {
          toastManager.add({ title: "Feedback sent", type: "success" });
          return true;
        }
        toastManager.add({ title: result?.message ?? "Failed to send feedback", type: "error" });
        return false;
      } catch (err) {
        toastMutationError(err, "Failed to send feedback");
        return false;
      }
    },
    [runSubmitFeedback, ready, hasFlag],
  );

  return { submitFeedback, loading };
}
